import type { BuildPlan, GuidedPhase } from '../types';

/**
 * What a guided-flow rewind does to the build plan — the webapp's PRE-FLIGHT
 * copy of the server's `rewind-consequence.ts` beside the rewind route, which
 * is the one that enforces it (an unconfirmed destructive rewind is refused
 * with 409 `REWIND_LOSES_PROGRESS`). A target before Planning DELETES the plan
 * outright; Planning or Review keeps it but resets every step to pending.
 * Files are kept in both cases, but the AI starts a new plan from scratch and
 * may rebuild them. Clicking "Idea" on the phase bar used to do the first
 * silently — an ATS session lost a 15-node plan with 13 verified nodes to one
 * click (2026-10-06). The two copies are pinned to each other by
 * `tests/architecture/builder-rewind-consequence-parity.test.ts`.
 */
export type RewindConsequence =
  | { kind: 'deletes-plan'; verifiedSteps: number; totalSteps: number }
  | { kind: 'resets-progress'; verifiedSteps: number; totalSteps: number };

export const GUIDED_PHASE_ORDER: Record<GuidedPhase, number> = {
  ideation: 0,
  clarification: 1,
  planning: 2,
  assumptions: 3,
  execution: 4,
  complete: 5,
};

export function rewindConsequence(
  targetPhase: GuidedPhase,
  buildPlan: BuildPlan | null | undefined,
): RewindConsequence | null {
  if (!buildPlan) return null;
  const items = buildPlan.graph ? buildPlan.graph.nodes : buildPlan.steps;
  const totalSteps = items.length;
  const verifiedSteps = items.filter((item) => item.status === 'verified').length;
  if (GUIDED_PHASE_ORDER[targetPhase] < GUIDED_PHASE_ORDER.planning) {
    return { kind: 'deletes-plan', verifiedSteps, totalSteps };
  }
  // Planning / Review keep the plan; only an approved plan has progress to lose.
  if (buildPlan.approved && GUIDED_PHASE_ORDER[targetPhase] < GUIDED_PHASE_ORDER.execution) {
    return { kind: 'resets-progress', verifiedSteps, totalSteps };
  }
  return null;
}

/**
 * The consequence the SERVER sent back on a refused rewind (409
 * `REWIND_LOSES_PROGRESS`), or null for any other error. Shape-checked, since
 * the body is whatever the API answered.
 */
export function rewindLossFromError(
  err: unknown,
): { consequence: RewindConsequence; version: number } | null {
  const body = (
    err as { body?: { error?: { code?: unknown; consequence?: unknown; version?: unknown } } }
  )?.body;
  if (body?.error?.code !== 'REWIND_LOSES_PROGRESS') return null;
  const c = body.error.consequence as Partial<RewindConsequence> | undefined;
  const version = body.error.version;
  if (
    !c ||
    (c.kind !== 'deletes-plan' && c.kind !== 'resets-progress') ||
    typeof c.verifiedSteps !== 'number' ||
    typeof c.totalSteps !== 'number' ||
    typeof version !== 'number'
  ) {
    return null;
  }
  return {
    consequence: { kind: c.kind, verifiedSteps: c.verifiedSteps, totalSteps: c.totalSteps },
    version,
  };
}
